import {
	LogOut,
	ShieldCheck,
	Coins,
	MessageSquare,
	Search,
	Plus,
	Home,
	Play,
	Folder,
	User,
	Sparkles,
	Crown,
	Settings,
	type LucideIcon,
} from "lucide-react";
import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PodhiveLogo } from "./taxpeerlogo.component";
import { NavigationService } from "@/lib/auth/navigation.service";
import type { NavLink } from "@/components/ui/navbar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/contexts/auth.context";
import { SystemRole } from "@/lib/auth/auth.types";
import { useCoinBalance } from "@/lib/coin/coin.store";
import { useSubscription } from "@/lib/subscription/useSubscription.hook";
import { SubscriptionService } from "@/lib/subscription/subscription.service";
import { FeedbackModal } from "@/components/feedbackModal.component";
import { usePodWorkspaces } from "@/features/dashboard/hooks/usePodWorkspaces.hook";
import { getPlayerActions, usePlayerStore } from "@/lib/player/player.store";
import { TrialCoinWindowIndicator } from "@/features/trial/TrialCoinWindowIndicator.component";
import { LOW_BALANCE_THRESHOLD } from "@/lib/nudges/nudge.types";
import { isTrialNudgeEligible } from "@/lib/nudges/useUpgradeNudge.hook";

// Map route paths to icons
const getIconForPath = (path: string): LucideIcon => {
	if (path.includes("/dashboard") || path === "/") return Home;
	if (path.includes("/explore")) return Search;
	if (path.includes("/podspace")) return Folder;
	if (path.includes("/podcast")) return Play;
	if (path.includes("/chat")) return User;
	return Home; // default fallback
};

/**
 * This component is the sidebar of the application.
 * It is used to display the navigation links and the library.
 * It is also used to display the logo and the title of the application.
 *
 */
export function Sidebar() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation("common");
	const { user, logout } = useAuth();
	const [navLinks, setNavLinks] = useState<NavLink[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const { balance: coinBalance, isLoading: isLoadingBalance } =
		useCoinBalance();
	const { subscriptionStatus, currentPlanId } = useSubscription();
	const hasPaidPlan =
		currentPlanId != null &&
		subscriptionStatus != null &&
		!["NONE", "CANCELED", "INCOMPLETE_EXPIRED"].includes(subscriptionStatus);
	const hasUsedTrial = user?.hasUsedSubscriptionTrial === true;
	const trialEligible = isTrialNudgeEligible(
		subscriptionStatus,
		user?.hasUsedSubscriptionTrial,
	);
	const isLowOrOutOfCoins =
		trialEligible &&
		coinBalance !== null &&
		coinBalance <= LOW_BALANCE_THRESHOLD;
	const safeNavLinks = Array.isArray(navLinks) ? navLinks : [];
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
	const [librarySearch, setLibrarySearch] = useState("");

	const { data: plans } = useQuery({
		queryKey: ["subscription-plans"],
		queryFn: () => SubscriptionService.getPlans(),
		staleTime: 1000 * 60 * 30,
	});
	const currentPlanName = plans?.find((p) => p.id === currentPlanId)?.name;

	// Get workspaces for library section
	const { workspaces, podcastsByWorkspace } = usePodWorkspaces();

	// Get currently playing podcast ID
	const currentlyPlayingPodcastId = usePlayerStore((state) => state.podcastId);

	// Filter and sort workspaces based on search
	const filteredWorkspaces = useMemo(() => {
		const filtered = workspaces.filter((ws) =>
			ws.title.toLowerCase().includes(librarySearch.toLowerCase()),
		);
		// Sort by creation date (newest first)
		return filtered.sort(
			(a, b) =>
				new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
		);
	}, [workspaces, librarySearch]);

	// Handle play button click
	const handlePlayPodcast = async (podcastId: string) => {
		try {
			// Check if this podcast is already loaded in the player
			if (podcastId === currentlyPlayingPodcastId) {
				// If it's the same podcast, just toggle play/pause
				getPlayerActions().togglePlay();
			} else {
				// If it's a different podcast, load and play it
				await getPlayerActions().loadPodcast(podcastId, true);
			}
		} catch (error) {
			console.error("Failed to play podcast:", error);
		}
	};

	// Helper function to get user initials
	const getUserInitials = () => {
		if (!user) return "U";

		if (user.displayName) {
			const nameParts = user.displayName.trim().split(/\s+/);
			if (nameParts.length >= 2) {
				return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
			}
			return nameParts[0].substring(0, 2).toUpperCase();
		}

		if (user.email) {
			return user.email.substring(0, 2).toUpperCase();
		}

		return "U";
	};

	// Check if user is a platform manager
	const isPlatformManager = user?.systemRoles?.includes(
		SystemRole.PLATFORM_MANAGER,
	);

	useEffect(() => {
		const fetchLinks = async () => {
			try {
				setIsLoading(true);
				const links = await NavigationService.getNavLinks();
				setNavLinks(links);
			} catch (err) {
				console.error("Failed to fetch navigation links", err);
				setNavLinks([]);
			} finally {
				setIsLoading(false);
			}
		};
		fetchLinks();
	}, []);

	// Coin balance is now managed by the Zustand store and auto-fetches

	const handleNavClick = (path: string) => {
		navigate(path);
	};

	return (
		<aside className="hidden md:flex w-64 shrink-0 bg-background-secondary overflow-hidden">
			<div className="flex h-full w-full flex-col p-4 overflow-hidden">
				{/* Logo */}
				<div className="pb-4 flex items-center justify-center gap-3 px-2">
					<PodhiveLogo width={75} bildmarke={true} />
				</div>

				{/* Navigation */}
				<nav className="space-y-1">
					{isLoading ? (
						<>
							<Button variant="ghost" className="w-full justify-start" disabled>
								Loading...
							</Button>
						</>
					) : (
						safeNavLinks.map((link) => {
							const isActive = location.pathname === link.href;
							const Icon = getIconForPath(link.href);
							return (
								<Button
									key={link.href}
									variant="ghost"
									className={`w-full [&_svg]:size-5 justify-start gap-3 transition-all py-1 rounded-lg font-regular ${
										isActive
											? "bg-primary/20 hover:bg-primary/30 text-primary-foreground font-normal"
											: "hover:bg-accent"
									}`}
									onClick={() => handleNavClick(link.href)}
								>
									<Icon
										className={`${isActive ? "text-primary-foreground" : ""}`}
										strokeWidth={isActive ? 2 : 1.5}
									/>
									{t(link.label)}
								</Button>
							);
						})
					)}
				</nav>

				{/* Library Section */}
				<div className="mt-6 flex-1 flex flex-col min-h-0 overflow-hidden">
					<div className="mb-3 flex items-center justify-between">
						<h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
							{t("sidebar.yourLibrary")}
						</h3>
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6 hover:text-primary-foreground"
							onClick={() => navigate("/dashboard")}
						>
							<Plus className="h-4 w-4" />
						</Button>
					</div>

					<div className="mb-3">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								type="text"
								placeholder={t("sidebar.searchInLibrary")}
								className="h-9 pl-9 text-sm"
								value={librarySearch}
								onChange={(e) => setLibrarySearch(e.target.value)}
							/>
						</div>
					</div>

					<div className="flex-1 overflow-y-auto overflow-x-hidden space-y-0.5 pb-2">
						{filteredWorkspaces.length > 0 ? (
							filteredWorkspaces.map((workspace) => {
								const podcast = podcastsByWorkspace[workspace.id];
								const isCurrentlyPlaying =
									podcast?.id === currentlyPlayingPodcastId;
								const canPlay = podcast?.status === "COMPLETED" && podcast?.id;

								// Format duration from milliseconds
								const durationInSeconds = podcast?.length
									? Math.floor(podcast.length / 1000)
									: 0;
								const minutes = Math.floor(durationInSeconds / 60);
								const seconds = durationInSeconds % 60;

								return (
									<button
										key={workspace.id}
										type="button"
										className={`w-full flex items-center gap-2 px-2 py-2.5 rounded-xl transition-all overflow-hidden ${
											isCurrentlyPlaying
												? "bg-primary/10 hover:bg-primary/25 border border-primary/80"
												: "hover:bg-accent/50"
										}`}
										onClick={() => {
											if (canPlay) {
												handlePlayPodcast(podcast.id);
											} else {
												// Navigate to podspace if podcast is still processing
												navigate(`/podspace`);
											}
										}}
									>
										<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded overflow-hidden bg-gradient-to-br from-orange-500/20 to-amber-500/20">
											<span className="text-base">🎙️</span>
										</div>
										<div className="flex-1 min-w-0 overflow-hidden text-left">
											<p
												className={`text-sm font-medium truncate overflow-hidden ${
													isCurrentlyPlaying
														? "text-primary-foreground"
														: "text-foreground"
												}`}
											>
												{workspace.title}
											</p>
											<p
												className={`text-xs truncate overflow-hidden ${
													isCurrentlyPlaying
														? "text-primary-foreground/80"
														: "text-muted-foreground"
												}`}
											>
												{podcast?.length && podcast.status === "COMPLETED"
													? `${minutes}:${String(seconds).padStart(2, "0")}`
													: t("sidebar.processing")}
											</p>
										</div>
									</button>
								);
							})
						) : (
							<div className="text-center py-8">
								<p className="text-sm text-muted-foreground">
									{librarySearch
										? t("sidebar.noResults")
										: t("sidebar.noWorkspaces")}
								</p>
							</div>
						)}
					</div>
				</div>
				<div className="mt-auto pt-4 space-y-2">
					{/* Upgrade / Plan Button — urgent only when coins are actually low */}
					{hasPaidPlan ? (
						<button
							type="button"
							onClick={() => navigate("/pricing")}
							className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-all"
						>
							<Crown className="h-3.5 w-3.5 text-orange-500" />
							{currentPlanName ?? t("sidebar.yourPlan")}
						</button>
					) : isLowOrOutOfCoins ? (
						<button
							type="button"
							onClick={() => navigate("/pricing")}
							className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-lg px-3 py-2.5 text-sm font-medium transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
						>
							<Sparkles className="h-4 w-4" />
							{coinBalance !== null && coinBalance <= 0
								? t("sidebar.outOfCoinsCta")
								: t("sidebar.lowCoinsCta")}
						</button>
					) : hasUsedTrial ? (
						<button
							type="button"
							onClick={() => navigate("/pricing")}
							className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-all"
						>
							<Crown className="h-3.5 w-3.5 text-orange-500" />
							{t("sidebar.upgradePlan")}
						</button>
					) : (
						<button
							type="button"
							onClick={() => navigate("/pricing")}
							className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-all"
						>
							<Sparkles className="h-3.5 w-3.5 text-orange-500" />
							{t("sidebar.startTrial")}
						</button>
					)}

					{/* Coin Balance — compact, amber when low */}
					<button
						type="button"
						onClick={() => navigate("/pricing")}
						className={`w-full flex items-center justify-center gap-1.5 px-2 py-1 text-xs transition-colors ${
							isLowOrOutOfCoins
								? "text-amber-600 dark:text-amber-400 hover:text-amber-700"
								: "text-muted-foreground hover:text-foreground"
						}`}
					>
						<Coins
							className={`h-3 w-3 ${isLowOrOutOfCoins ? "text-amber-500" : "text-orange-500/70"}`}
						/>
						{isLoadingBalance ? (
							<span>...</span>
						) : coinBalance !== null ? (
							<span>
								{coinBalance.toLocaleString()} {t("sidebar.coins")}
							</span>
						) : (
							<span>N/A</span>
						)}
					</button>

					{/* Trial rolling-cap indicator (TRIALING users only) */}
					<div className="w-full flex justify-center">
						<TrialCoinWindowIndicator />
					</div>

					{/* User Menu */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								className="h-auto w-full justify-start gap-3 px-2 py-3 hover:bg-accent/50 transition-colors group"
							>
								<Avatar className="size-10 ring-2 ring-transparent group-hover:ring-orange-500/20 transition-all">
									<AvatarFallback className="bg-gradient-to-br from-orange-500 to-amber-500 text-white font-semibold text-sm">
										{getUserInitials()}
									</AvatarFallback>
								</Avatar>
								<div className="flex flex-col min-w-0 flex-1 text-left">
									<span className="text-sm font-medium text-foreground truncate">
										{user?.displayName || user?.email?.split("@")[0] || "User"}
									</span>
									<span className="text-xs text-muted-foreground truncate">
										{user?.email || ""}
									</span>
								</div>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent side="top" align="end" className="w-56 mb-2">
							{isPlatformManager && (
								<>
									<DropdownMenuItem
										onClick={() => navigate("/platform-manager")}
									>
										<ShieldCheck className="mr-2 h-4 w-4" />
										{t("mobileNav.platformManager")}
									</DropdownMenuItem>
									<DropdownMenuSeparator />
								</>
							)}
							<DropdownMenuItem onClick={() => navigate("/settings")}>
								<Settings className="mr-2 h-4 w-4" />
								{t("sidebar.settings")}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => setIsFeedbackModalOpen(true)}>
								<MessageSquare className="mr-2 h-4 w-4" />
								{t("mobileNav.sendFeedback")}
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={logout}>
								<LogOut className="mr-2 h-4 w-4" />
								{t("logout.logout")}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			{/* Feedback Modal */}
			<FeedbackModal
				open={isFeedbackModalOpen}
				onOpenChange={setIsFeedbackModalOpen}
			/>
		</aside>
	);
}
